import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Progress,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import {
  ClearOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AiClipperMetricsFilter,
  AiClipCandidateApprovalSource,
  AiClipperSourceType,
  AiClipperTimeSeriesGranularity,
  AiClipperUserBreakdownRow,
  PostCandidateStatus,
} from 'api/aiClipperMetrics/types';

import styles from './AIClipperMetrics.module.scss';
import KpiCard from './KpiCard';
import { userColumns } from './userColumns';
import {
  APPROVAL_SOURCE_OPTIONS,
  CHART_SERIES,
  DateRange,
  GRANULARITY_ALIGNMENT,
  TIME_SERIES_BUCKET_HINT,
  SOURCE_TYPE_OPTIONS,
  STATUS_OPTIONS,
  formatBucketLabel,
  withInterviewMetricsSourceTypes,
  formatRatePct,
  sourceTypeLabel,
} from './helpers';
import { useClipperMetricsDashboard } from './useClipperMetricsDashboard';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ERROR_MESSAGES = {
  summary: 'Failed to load metrics summary.',
  userBreakdown: 'Failed to load athlete breakdown.',
  series: 'Failed to load time series.',
};

const AIClipperMetrics: React.FC = () => {
  // Each input owns its own state so changing one doesn't disturb the others.
  const [statuses, setStatuses] = useState<PostCandidateStatus[]>([]);
  const [sourceTypes, setSourceTypes] = useState<AiClipperSourceType[]>([]);
  const [approvalSources, setApprovalSources] = useState<
    AiClipCandidateApprovalSource[]
  >([]);

  // Sorted keys keep the filter identity stable when only the selection order
  // changes, and are the values the filter is built from.
  const statusesKey = useMemo(() => [...statuses].sort().join(','), [statuses]);
  const sourceTypesKey = useMemo(
    () => [...sourceTypes].sort().join(','),
    [sourceTypes]
  );
  const approvalSourcesKey = useMemo(
    () => [...approvalSources].sort().join(','),
    [approvalSources]
  );

  const extraFilter = useMemo<AiClipperMetricsFilter>(() => {
    const next: AiClipperMetricsFilter = {
      sourceTypes: withInterviewMetricsSourceTypes(
        sourceTypesKey
          ? (sourceTypesKey.split(',') as AiClipperSourceType[])
          : []
      ),
    };
    if (statusesKey)
      next.statuses = statusesKey.split(',') as PostCandidateStatus[];
    if (approvalSourcesKey)
      next.approvalSources = approvalSourcesKey.split(
        ','
      ) as AiClipCandidateApprovalSource[];
    return next;
  }, [statusesKey, sourceTypesKey, approvalSourcesKey]);

  const handleClearExtraFilters = useCallback(() => {
    setStatuses([]);
    setSourceTypes([]);
    setApprovalSources([]);
  }, []);

  const {
    athleteInput,
    range,
    setRange,
    granularity,
    setGranularity,
    baseFiltersDirty,
    summary,
    summaryLoading,
    userRows,
    userTotal,
    userTableLoading,
    userPage,
    setUserPage,
    userPageSize,
    setUserPageSize,
    seriesResult,
    seriesLoading,
    error,
    setError,
    handleRefresh,
    handleClear,
    handleAthleteChange,
  } = useClipperMetricsDashboard({
    extraFilter,
    errorMessages: ERROR_MESSAGES,
    onClear: handleClearExtraFilters,
  });

  const approvalPct = summary.acceptRatePctOfReviewed;
  const approvalBarValue = approvalPct ?? 0;

  const filtersDirty =
    baseFiltersDirty ||
    statuses.length > 0 ||
    sourceTypes.length > 0 ||
    approvalSources.length > 0;

  const chartData = useMemo(
    () =>
      seriesResult.points.map((point) => ({
        label: formatBucketLabel(point.bucketStart, granularity),
        'Manual approvals': point.manualAccepted,
        'System approvals': point.systemAccepted,
        'Total approvals': point.accepted,
      })),
    [seriesResult.points, granularity]
  );

  // One stable selector per KPI keeps React.memo on KpiCard effective.
  const selectKpiFilter = useCallback(
    (
        nextStatuses: PostCandidateStatus[],
        nextApprovalSources: AiClipCandidateApprovalSource[] = []
      ) =>
      () => {
        setStatuses(nextStatuses);
        setApprovalSources(nextApprovalSources);
      },
    []
  );
  const kpiCards: {
    title: string;
    value: number;
    variantClass?: string;
    statuses: PostCandidateStatus[];
    approvalSources?: AiClipCandidateApprovalSource[];
  }[] = useMemo(
    () => [
      { title: 'Total generated', value: summary.totalGenerated, statuses: [] },
      {
        title: 'Accepted',
        value: summary.accepted,
        variantClass: styles.kpiAccepted,
        statuses: ['Accepted'],
      },
      {
        title: 'Manual accepted',
        value: summary.manualAccepted,
        variantClass: styles.kpiAccepted,
        statuses: ['Accepted'],
        approvalSources: ['Manual'],
      },
      {
        title: 'System accepted',
        value: summary.systemAccepted,
        variantClass: styles.kpiAccepted,
        statuses: ['Accepted'],
        approvalSources: ['System'],
      },
      {
        title: 'Rejected',
        value: summary.rejected,
        variantClass: styles.kpiRejected,
        statuses: ['Rejected'],
      },
      {
        title: 'Pending review',
        value: summary.notReviewed,
        variantClass: styles.kpiPending,
        statuses: ['NotReviewed'],
      },
    ],
    [summary]
  );

  const isStatusActive = (target: PostCandidateStatus[]) =>
    target.length === statuses.length &&
    target.every((s, i) => statuses[i] === s);

  const isApprovalSourceActive = (
    target: AiClipCandidateApprovalSource[] = []
  ) =>
    target.length === approvalSources.length &&
    target.every((s, i) => approvalSources[i] === s);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Title level={4} className={styles.title}>
            AI Clipper Metrics
          </Title>
          <Text type="secondary">
            Approval performance for AI-generated clip candidates.
          </Text>
        </div>
        <div className={styles.filterBar}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Athlete slug or name…"
            value={athleteInput}
            onChange={handleAthleteChange}
            allowClear
            style={{ width: 240 }}
          />
          <RangePicker
            value={range as any}
            onChange={(next) => setRange(next as DateRange)}
            allowClear
          />
          <Select<PostCandidateStatus[]>
            mode="multiple"
            placeholder="Status"
            value={statuses}
            onChange={(next) => setStatuses(next ?? [])}
            options={STATUS_OPTIONS}
            style={{ minWidth: 200 }}
            allowClear
            maxTagCount="responsive"
          />
          <Select<AiClipperSourceType[]>
            mode="multiple"
            placeholder="Source (all)"
            value={sourceTypes}
            onChange={(next) => setSourceTypes(next ?? [])}
            options={SOURCE_TYPE_OPTIONS}
            style={{ minWidth: 200 }}
            allowClear
            maxTagCount="responsive"
          />
          <Select<AiClipCandidateApprovalSource[]>
            mode="multiple"
            placeholder="Approval source"
            value={approvalSources}
            onChange={(next) => setApprovalSources(next ?? [])}
            options={APPROVAL_SOURCE_OPTIONS}
            style={{ minWidth: 200 }}
            allowClear
            maxTagCount="responsive"
          />
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={!filtersDirty}
            >
              Clear
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={summaryLoading || userTableLoading || seriesLoading}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <div className={styles.kpiRow}>
        {kpiCards.map((card) => (
          <KpiCard
            key={card.title}
            title={card.title}
            value={card.value}
            loading={summaryLoading}
            variantClass={card.variantClass}
            active={
              isStatusActive(card.statuses) &&
              isApprovalSourceActive(card.approvalSources)
            }
            onClick={selectKpiFilter(card.statuses, card.approvalSources)}
          />
        ))}
      </div>

      <div className={styles.approvalCard}>
        <div>
          <div className={styles.approvalLabel}>
            Approval rate (of reviewed)
          </div>
          <div className={styles.approvalValue}>
            {summaryLoading ? '…' : formatRatePct(approvalPct)}
          </div>
        </div>
        <div className={styles.approvalProgress}>
          <Progress
            percent={Number(approvalBarValue.toFixed(1))}
            strokeColor="#16a34a"
            trailColor="rgba(0,0,0,0.06)"
            showInfo={false}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {summary.accepted} accepted of {summary.accepted + summary.rejected}{' '}
            reviewed ({summary.manualAccepted} manual, {summary.systemAccepted}{' '}
            system, {summary.notReviewed} pending)
          </Text>
        </div>
      </div>

      <div className={styles.chartCard} style={{ marginBottom: 16 }}>
        <div className={styles.chartTitle} style={{ marginBottom: 12 }}>
          By source
        </div>
        <Table<{
          sourceType: AiClipperSourceType;
          totalGenerated: number;
          accepted: number;
          manualAccepted: number;
          systemAccepted: number;
          rejected: number;
          notReviewed: number;
        }>
          rowKey="sourceType"
          size="small"
          loading={summaryLoading}
          pagination={false}
          dataSource={summary.sourceBreakdown}
          locale={{ emptyText: 'No candidates for the current filter.' }}
          columns={[
            {
              title: 'Source',
              dataIndex: 'sourceType',
              key: 'sourceType',
              render: (value: AiClipperSourceType) => sourceTypeLabel(value),
            },
            {
              title: 'Total generated',
              dataIndex: 'totalGenerated',
              key: 'totalGenerated',
            },
            { title: 'Accepted', dataIndex: 'accepted', key: 'accepted' },
            {
              title: 'Manual',
              dataIndex: 'manualAccepted',
              key: 'manualAccepted',
            },
            {
              title: 'System',
              dataIndex: 'systemAccepted',
              key: 'systemAccepted',
            },
            { title: 'Rejected', dataIndex: 'rejected', key: 'rejected' },
            {
              title: 'Pending',
              dataIndex: 'notReviewed',
              key: 'notReviewed',
            },
          ]}
        />
      </div>

      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <div>
            <div className={styles.chartTitle}>Approvals over time</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Buckets aligned to {GRANULARITY_ALIGNMENT[granularity]}.{' '}
              {TIME_SERIES_BUCKET_HINT}
            </Text>
          </div>
          <Radio.Group
            value={granularity}
            onChange={(e) =>
              setGranularity(e.target.value as AiClipperTimeSeriesGranularity)
            }
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="Day">Day</Radio.Button>
            <Radio.Button value="Week">Week</Radio.Button>
            <Radio.Button value="Month">Month</Radio.Button>
            <Radio.Button value="Year">Year</Radio.Button>
          </Radio.Group>
        </div>
        {seriesResult.truncated && (
          <Alert
            type="warning"
            message="Showing the first 260 buckets — narrow the date range or use a coarser granularity to see the full series."
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        <div className={styles.chartBody}>
          {seriesLoading ? (
            <div className={styles.chartLoading}>
              <Spin />
            </div>
          ) : chartData.length === 0 ? (
            <div className={styles.chartEmpty}>
              <Text type="secondary">No candidates in this range.</Text>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.06)"
                />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                {CHART_SERIES.map(({ dataKey, stroke }) => (
                  <Line
                    key={dataKey}
                    type="monotone"
                    dataKey={dataKey}
                    stroke={stroke}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <Table<AiClipperUserBreakdownRow>
        rowKey={(r) => r.userId}
        columns={userColumns}
        dataSource={userRows}
        loading={userTableLoading}
        pagination={{
          current: userPage,
          pageSize: userPageSize,
          total: userTotal,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (newPage, newPageSize) => {
            if (newPageSize !== userPageSize) {
              setUserPageSize(newPageSize);
              setUserPage(1);
            } else {
              setUserPage(newPage);
            }
          },
        }}
      />
    </div>
  );
};

export default AIClipperMetrics;
